import React, { useEffect, useReducer, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Drawer,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useIsMobile } from 'src/hooks/useIsMobile';
import { handleErrorToast } from 'src/components/molecules/Toast/Toast';
import { PREVIEW_SCREENS, QuestionData } from 'src/types/formQuestionnaire';
import { LanguageOption } from 'src/utils/languages';
import { FormPreviewCardContent } from './FormPreviewCardContent';
import { formatValidationError } from 'src/utils/validateFileUpload/fileValidation';
import { initialPreviewState, previewReducer } from './FormPreview.reducer';
import {
  ContentTypePill,
  InlineContentWrapper,
  InlineHeader,
  InlineSection,
  InlineTitle,
  InlineTitleBlock,
  LanguageBlock,
  LanguageRow,
  LanguageSection,
  PreviewContent,
} from './FormPreview.styles';
import {
  createPreviewHandlers,
  getAnsweredCount,
  MAX_PREVIEW_FILE_SIZE_MB,
  MAX_PREVIEW_FORM_SIZE_MB,
  resolveReachableQuestions,
} from './helpers';
import { localizeQuestionDataForLocale } from './localizeQuestionDataForLocale';
import {
  FormPreviewInlinePlacement,
  FormPreviewVariant,
  PreviewQuestionMinimal,
} from './types';
import { getFormPreviewTranslation } from './translations';

export interface FormPreviewBaseProps {
  variant: FormPreviewVariant;
  readOnly?: boolean;
  title?: string;
  description?: string;
  languageOptions: Array<{ value: string; label: string }>;
  questions: PreviewQuestionMinimal[] | QuestionData[];
  showEmployeeFacingPill?: boolean;
  /** Layout for inline variant; ignored for slideout. */
  inlinePlacement?: FormPreviewInlinePlacement;
  /** When true, an overlay loader is shown over the preview area. */
  isTranslating?: boolean;
  'data-testid'?: string;
}

export interface FormPreviewSlideoutProps extends FormPreviewBaseProps {
  variant: 'slideout';
  readOnly?: false;
  isOpen: boolean;
  onClose: (isOpen: boolean) => void;
  questions: QuestionData[];
  selectedLanguage?: string;
  onLanguageChange?: (language: string) => void;
}

export interface FormPreviewInlineReadOnlyProps extends FormPreviewBaseProps {
  variant: 'inline';
  readOnly: true;
  selectedLanguage: string;
  onLanguageChange: (language: string) => void;
  questions: PreviewQuestionMinimal[];
}

export interface FormPreviewInlineInteractiveProps extends FormPreviewBaseProps {
  variant: 'inline';
  readOnly?: false;
  selectedLanguage: string;
  onLanguageChange: (language: string) => void;
  questions: QuestionData[];
}

export type FormPreviewInlineProps =
  | FormPreviewInlineReadOnlyProps
  | FormPreviewInlineInteractiveProps;

export type FormPreviewProps =
  | FormPreviewSlideoutProps
  | FormPreviewInlineProps;

export const FormPreview = (props: FormPreviewProps) => {
  const theme = useTheme();
  const emptyStateSrc = theme.isVaultVariant
    ? '/assets/empty-state.svg'
    : '/assets/empty-state-atlas.svg';
  const {
    variant,
    readOnly = false,
    title: _title = '',
    description: _description = '',
    languageOptions,
    questions,
    showEmployeeFacingPill = false,
    inlinePlacement = 'standard',
    isTranslating = false,
    'data-testid': dataTestId = 'form-preview',
  } = props;

  const sortedQuestions = React.useMemo(
    () => [...questions].sort((a, b) => a.order - b.order),
    [questions],
  );

  const [state, dispatch] = useReducer(previewReducer, initialPreviewState);
  const { screen, currentQuestionIndex, answers } = state;
  const [currentLanguage, setCurrentLanguage] = useState<LanguageOption | null>(
    null,
  );

  const selectedLanguageFromPropsForLocalize =
    variant === 'inline' && 'selectedLanguage' in props
      ? props.selectedLanguage
      : variant === 'slideout' &&
          'selectedLanguage' in props &&
          props.selectedLanguage != null
        ? props.selectedLanguage
        : null;

  const defaultLanguageOptionForLocalize =
    languageOptions.find(
      (option) =>
        option.value === 'en-GB' ||
        option.label.trim().toLowerCase() === 'english',
    ) ??
    languageOptions[0] ??
    null;

  const activeLocaleCode =
    selectedLanguageFromPropsForLocalize ??
    currentLanguage?.value ??
    defaultLanguageOptionForLocalize?.value ??
    null;

  const textInputPlaceholder = getFormPreviewTranslation(
    'textInputPlaceholder',
    activeLocaleCode,
  );
  const dropdownPlaceholder = getFormPreviewTranslation(
    'dropdownPlaceholder',
    activeLocaleCode,
  );
  const databaseFieldLabel = getFormPreviewTranslation(
    'databaseFieldLabel',
    activeLocaleCode,
  );
  const databaseFieldPlaceholder = getFormPreviewTranslation(
    'databaseFieldPlaceholder',
    activeLocaleCode,
  );

  const [readOnlyStep, setReadOnlyStep] = useState(0);

  useEffect(() => {
    if (readOnly) setReadOnlyStep(0);
  }, [readOnly, questions]);

  const questionsIdentityKey = React.useMemo(
    () => sortedQuestions.map((q) => q.id ?? q.order).join('|'),
    [sortedQuestions],
  );
  useEffect(() => {
    if (!readOnly) dispatch({ type: 'RESET_ALL' });
  }, [questionsIdentityKey, readOnly]);

  const fileInputId = useRef(
    `file-upload-${Math.random().toString(36).slice(2)}`,
  );

  const isSlideout = variant === 'slideout';
  const isInline = variant === 'inline';
  const isMobile = useIsMobile();

  const handleClose = () => {
    if (isSlideout && 'onClose' in props) {
      props.onClose(false);
      dispatch({ type: 'RESET_ALL' });
      setCurrentLanguage(null);
    }
  };

  const hasQuestions = sortedQuestions.length > 0;

  const currentQuestionInteractiveRaw = hasQuestions
    ? (sortedQuestions[currentQuestionIndex] as QuestionData)
    : null;
  const currentQuestionInteractive = currentQuestionInteractiveRaw
    ? localizeQuestionDataForLocale(
        currentQuestionInteractiveRaw,
        activeLocaleCode,
      )
    : null;
  const currentQuestionReadOnly = hasQuestions
    ? (sortedQuestions[readOnlyStep] as PreviewQuestionMinimal)
    : null;

  const reachableQuestions = React.useMemo(
    () =>
      !readOnly && hasQuestions
        ? resolveReachableQuestions(sortedQuestions as QuestionData[], answers)
        : [],
    [readOnly, hasQuestions, sortedQuestions, answers],
  );
  const currentQuestionReachableIndex = reachableQuestions.findIndex(
    (q) => q.id === currentQuestionInteractive?.id,
  );
  const questionsCountInteractive =
    reachableQuestions.length > 0
      ? reachableQuestions.length
      : sortedQuestions.length;
  const currentQuestionPositionInteractive =
    currentQuestionReachableIndex >= 0
      ? currentQuestionReachableIndex + 1
      : currentQuestionIndex + 1;

  const isLastQuestion =
    (currentQuestionReachableIndex >= 0 &&
      currentQuestionReachableIndex === reachableQuestions.length - 1) ||
    readOnlyStep === sortedQuestions.length - 1;

  const uploadIntervalsRef = useRef(new Set<ReturnType<typeof setInterval>>());

  useEffect(() => {
    const intervals = uploadIntervalsRef.current;
    return () => {
      intervals.forEach((id) => clearInterval(id));
      intervals.clear();
    };
  }, []);

  const handleFileValidationError = (invalidFiles: string[]) => {
    const errorDescription = formatValidationError(invalidFiles);
    handleErrorToast({
      toastMessage: 'Unsupported file type',
      toastDescription: errorDescription,
      autoClose: 5000,
    });
  };

  const handleFileSizeError = (oversizedFiles: string[]) => {
    const fileList = oversizedFiles.join(', ');
    handleErrorToast({
      toastMessage: `File size exceeds ${MAX_PREVIEW_FILE_SIZE_MB} MB limit`,
      toastDescription: fileList,
      autoClose: 5000,
    });
  };

  const handleFormSizeError = (files: string[]) => {
    const fileList = files.join(', ');
    handleErrorToast({
      toastMessage: `Total attachments exceed ${MAX_PREVIEW_FORM_SIZE_MB} MB limit for this form`,
      toastDescription: fileList,
      autoClose: 5000,
    });
  };

  const handlers = createPreviewHandlers(
    dispatch,
    currentQuestionInteractive?.id ?? null,
    screen,
    currentQuestionIndex,
    isLastQuestion,
    uploadIntervalsRef.current,
    handleFileValidationError,
    handleFileSizeError,
    handleFormSizeError,
    {
      questions: sortedQuestions as QuestionData[],
      answers,
    },
  );

  const answeredCount = getAnsweredCount(answers, reachableQuestions);
  const progressInteractive =
    questionsCountInteractive > 0
      ? (answeredCount / questionsCountInteractive) * 100
      : 0;
  const progressReadOnly =
    sortedQuestions.length > 0
      ? ((readOnlyStep + 1) / sortedQuestions.length) * 100
      : 0;

  const showBackButton =
    (screen === PREVIEW_SCREENS.QUESTIONS &&
      !readOnly &&
      currentQuestionIndex > 0) ||
    (readOnly && readOnlyStep > 0);
  const showNextButton =
    (!readOnly &&
      hasQuestions &&
      screen === PREVIEW_SCREENS.QUESTIONS &&
      !isLastQuestion) ||
    (readOnly && readOnlyStep < sortedQuestions.length - 1);

  const handleNextReadOnly = () => {
    if (readOnlyStep < sortedQuestions.length - 1) {
      setReadOnlyStep((s) => s + 1);
    }
  };
  const handleBackReadOnly = () => {
    if (readOnlyStep > 0) setReadOnlyStep((s) => s - 1);
  };

  const selectedLanguageFromProps = selectedLanguageFromPropsForLocalize;

  const defaultLanguageOption = defaultLanguageOptionForLocalize;

  const languageOptionForSelect =
    selectedLanguageFromProps != null
      ? (languageOptions.find((o) => o.value === selectedLanguageFromProps) ??
        (defaultLanguageOption
          ? {
              value: defaultLanguageOption.value,
              label: defaultLanguageOption.label,
            }
          : null))
      : (currentLanguage ??
        (defaultLanguageOption
          ? {
              value: defaultLanguageOption.value,
              label: defaultLanguageOption.label,
            }
          : null));

  const handleLanguageChange = (
    option: { value: string; label: string } | null,
  ) => {
    if (option) {
      const value = option.value;
      if (isInline && 'onLanguageChange' in props) {
        props.onLanguageChange(value);
      } else if (
        isSlideout &&
        'onLanguageChange' in props &&
        props.onLanguageChange
      ) {
        props.onLanguageChange(value);
        setCurrentLanguage(option as LanguageOption);
      } else {
        setCurrentLanguage(option as LanguageOption);
      }
    }
  };

  const renderCardContent = () => (
    <FormPreviewCardContent
      readOnly={readOnly}
      isTranslating={isTranslating}
      inlinePlacement={inlinePlacement}
      dataTestId={dataTestId}
      sortedQuestions={sortedQuestions}
      readOnlyStep={readOnlyStep}
      currentQuestionReadOnly={currentQuestionReadOnly}
      currentQuestionInteractive={currentQuestionInteractive}
      screen={screen}
      answers={answers}
      dispatch={dispatch}
      fileInputId={fileInputId.current}
      emptyStateSrc={emptyStateSrc}
      hasQuestions={hasQuestions}
      progressReadOnly={progressReadOnly}
      progressInteractive={progressInteractive}
      showBackButton={showBackButton}
      showNextButton={showNextButton}
      questionsCountInteractive={questionsCountInteractive}
      currentQuestionPositionInteractive={currentQuestionPositionInteractive}
      handleBackReadOnly={handleBackReadOnly}
      handleNextReadOnly={handleNextReadOnly}
      handlers={handlers}
      textInputPlaceholder={textInputPlaceholder}
      dropdownPlaceholder={dropdownPlaceholder}
      databaseFieldLabel={databaseFieldLabel}
      databaseFieldPlaceholder={databaseFieldPlaceholder}
    />
  );

  if (isSlideout) {
    return (
      <Drawer
        anchor="right"
        open={'isOpen' in props ? props.isOpen : false}
        onClose={handleClose}
        data-testid={`${dataTestId}-slideout`}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-labelledby': 'form-preview-title',
          },
        }}
        sx={{
          zIndex: 4000,
          '& .MuiDrawer-paper': {
            width: isMobile ? '100%' : 675,
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ flexShrink: 0, pt: 3, px: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography
              id="form-preview-title"
              component="h2"
              sx={{ fontWeight: 600, fontSize: '30px', lineHeight: '38px' }}
            >
              Preview
            </Typography>
            <IconButton
              onClick={handleClose}
              aria-label="Close preview"
              data-testid={`${dataTestId}-close`}
              size="medium"
              sx={{ mt: '-4px', mr: '-8px' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          minHeight={0}
          overflow="auto"
          pt={3}
          px={3}
          pb={3}
        >
          <PreviewContent data-testid={`${dataTestId}-content`}>
            <LanguageSection data-testid={`${dataTestId}-language-section`}>
              <FormControl fullWidth>
                <FormLabel
                  id={`${dataTestId}-language-label-id`}
                  data-testid={`${dataTestId}-language-label`}
                >
                  Languages
                </FormLabel>
                <Select
                  labelId={`${dataTestId}-language-label-id`}
                  value={languageOptionForSelect?.value ?? ''}
                  onChange={(e: SelectChangeEvent) => {
                    const opt = languageOptions.find(
                      (o) => o.value === e.target.value,
                    );
                    if (opt) handleLanguageChange(opt);
                  }}
                  data-testid={`${dataTestId}-language-dropdown`}
                  MenuProps={{ disablePortal: true }}
                >
                  {languageOptions.map((o) => (
                    <MenuItem
                      key={o.value}
                      value={o.value}
                      data-testid={`${o.label}-select`}
                    >
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </LanguageSection>
            {renderCardContent()}
          </PreviewContent>
        </Box>
      </Drawer>
    );
  }

  return (
    <InlineSection $inlinePlacement={inlinePlacement} data-testid={dataTestId}>
      <InlineHeader $inlinePlacement={inlinePlacement}>
        <InlineTitleBlock>
          <InlineTitle
            $inlinePlacement={inlinePlacement}
            data-testid={`${dataTestId}-title`}
          >
            {inlinePlacement === 'formManagement'
              ? 'Form preview'
              : 'Questions Preview'}
          </InlineTitle>
        </InlineTitleBlock>
        <LanguageRow $inlinePlacement={inlinePlacement}>
          <LanguageBlock $inlinePlacement={inlinePlacement}>
            {(languageOptions.length > 1 ||
              (inlinePlacement === 'formManagement' &&
                languageOptions.length >= 1)) && (
              <>
                <FormControl fullWidth>
                  <FormLabel
                    id={`${dataTestId}-inline-language-label`}
                    data-testid={`${dataTestId}-language-label`}
                  >
                    Languages
                  </FormLabel>
                  <Select
                    labelId={`${dataTestId}-inline-language-label`}
                    value={languageOptionForSelect?.value ?? ''}
                    onChange={(e: SelectChangeEvent) => {
                      const opt = languageOptions.find(
                        (o) => o.value === e.target.value,
                      );
                      if (opt) handleLanguageChange(opt);
                    }}
                    data-testid={`${dataTestId}-language-dropdown`}
                    MenuProps={{ disablePortal: true }}
                  >
                    {languageOptions.map((o) => (
                      <MenuItem
                        key={o.value}
                        value={o.value}
                        data-testid={`${o.label}-select`}
                      >
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </LanguageBlock>
          {showEmployeeFacingPill && (
            <ContentTypePill>Employee facing content</ContentTypePill>
          )}
        </LanguageRow>
      </InlineHeader>
      <InlineContentWrapper
        $inlinePlacement={inlinePlacement}
        data-testid={`${dataTestId}-content`}
      >
        {renderCardContent()}
      </InlineContentWrapper>
    </InlineSection>
  );
};
